import React, { useState } from 'react';
import Box from '@amzn/meridian/box';
import DatePicker from '@amzn/meridian/date-picker';
import Select, { SelectOption } from '@amzn/meridian/select';
import Heading from '@amzn/meridian/heading';
import Text from '@amzn/meridian/text';
import Row from '@amzn/meridian/row';
import Column from '@amzn/meridian/column';
import Button from '@amzn/meridian/button';
import Link from '@amzn/meridian/link';
import Icon from '@amzn/meridian/icon';
import Image from '@amzn/meridian/image';
import infoKnockoutTokens from '@amzn/meridian-tokens/base/icon/info-knockout';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  setPlanStatus,
  setGenerationResult,
  setStationAssignmentData,
  selectStationAssignment,
} from '../../store/slices/stationAssignmentSlice';
import { showNotification } from '../../store/slices/errorSlice';
import { PlanStatus, ROUTES, DEFAULT_SHIFT_OPTIONS } from '../../utils/constants';
import { createShiftPlan, getClockWindowNameBasedOnSelectedOption } from '../../services/shiftPlanService';
import { PlanTypes, PlanVersion } from '../../types/shiftPlan';
import { getTodayDate } from './models';
import { isBlankString } from '../../utils/string-utils';
import raptorSvg from '../../assets/images/raptor.svg';
import './index.scss';

const StationAssignment: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { planStatus } = useAppSelector(selectStationAssignment);

  const [selectedDate, setSelectedDate] = useState<string>(getTodayDate());
  const [selectedShift, setSelectedShift] = useState<string>('day');
  const [isGenerating, setIsGenerating] = useState<boolean>(false);

  const handleDateChange = (date: string) => {
    setSelectedDate(date);
  };

  const handleShiftChange = (shift: string) => {
    setSelectedShift(shift);
  };

  const handleGenerate = async () => {
    if (isBlankString(selectedDate) || isBlankString(selectedShift)) {
      dispatch(showNotification({
        title: 'Missing Selection',
        message: 'Please select both date and shift before generating station assignment.',
        type: 'warning',
      }));
      return;
    }

    setIsGenerating(true);
    dispatch(setPlanStatus(PlanStatus.PENDING_CREATION));

    const clockWindowName = getClockWindowNameBasedOnSelectedOption(selectedShift);

    // Create shift plan
    const createResult = await createShiftPlan(selectedDate, clockWindowName, PlanTypes.PRE_SHIFT, PlanVersion.RELO_STATION_ASSIGNMENT_V1);

    if (!createResult.planId || createResult.status === PlanStatus.FAILED) {
      setIsGenerating(false);
      dispatch(setPlanStatus(PlanStatus.FAILED));
      return;
    }

    dispatch(setGenerationResult({
      planId: createResult.planId,
      planStatus: createResult.status as PlanStatus,
    }));
    dispatch(setStationAssignmentData({ date: selectedDate, shift: selectedShift }));
    navigate(ROUTES.SHIFT_ROTATION_SHIFT_ROSTER);

    setIsGenerating(false);
  };

  const getInfoMessage = (): string => {
    if (isGenerating || planStatus === PlanStatus.PENDING_CREATION) {
      return 'Station assignment is being generated...';
    }
    return 'Station assignment has not been generated.';
  };

  return (
    <Box spacingInset="m" height="100%">
      {/* Header with title and View history link */}
      <Row spacing="300" alignmentVertical="center">
        <Heading level={1}>Station assignment</Heading>
        <Link href="#" type="secondary">View history</Link>
      </Row>
      <Text color="secondary">
        This page allows you to run and view the next shift station assignments and accept, adjust or override it.
      </Text>

      {/* Input controls box */}
      <Box className="station-assignment-inputs-box" spacingInset="400" type="outline">
        <Row spacing="500" alignmentVertical="bottom" widths="fit">
          <Column spacing="200">
            <Text type="b200" tag="label">Date</Text>
            <DatePicker
              value={selectedDate}
              onChange={handleDateChange}
            />
          </Column>

          <Column spacing="200">
            <Text type="b200" tag="label">Shift</Text>
            <Select
              value={selectedShift}
              onChange={handleShiftChange}
            >
              {DEFAULT_SHIFT_OPTIONS.map((option) => (
                <SelectOption key={option.value} value={option.value} label={option.label} />
              ))}
            </Select>
          </Column>
        </Row>
      </Box>

      {/* Status box with info message and generate button */}
      <Box className="station-assignment-status-box" spacingInset="400" type="outline">
        <Row spacing="400" alignmentVertical="center" widths={['fill', 'fit']}>
          <Row spacing="300" alignmentVertical="center">
            <Icon tokens={infoKnockoutTokens} color="secondary" />
            <Text color="secondary">{getInfoMessage()}</Text>
          </Row>
          <Button
            onClick={handleGenerate}
            disabled={isGenerating}
          >
            {isGenerating ? "Generating..." : "Generate Station assignment"}
          </Button>
        </Row>
      </Box>

      {/* Decorative Raptor SVG in lower right corner */}
      <div className="decorative-raptor">
        <Image
          src={raptorSvg}
          viewportHeight={200}
          viewportAspectRatio={1}
        >
          ReLo Raptor
        </Image>
      </div>
    </Box>
  );
};

export default StationAssignment;

import './index.scss';